import Api from 'fou-api'
import VuexErrors from 'src/vuex/modules/errors'
import Errors from 'src/utils/helper/errors'
import Misc, { getSessionFoundryAiConsentFromLS } from 'src/utils/helper/misc'
import { EMAIL_VERIFY_BANNER_DISMISSED_STORAGE_KEY } from 'src/utils/constants/common.js';
import { getEmailVerifiedFromLocalStorage, persistLocalStorageFromProfileResponse } from 'src/utils/helper/localStorage.js';
import { updateUserInfoObjKey } from '../../../../../modules/adobe-cja/utils.js'
import { CONSOLIDATED, NAV_LOCK_LEVEL } from 'src/utils/constants/contract-termination'
import { getNavLockLevel } from 'src/utils/helper/contractTermination'

export default {
  namespaced: true,

  state() {
    return {
      user: {
        authenticated: false,
        // foundries: [],   // for use in later versions
        name: '',
        displayName: '',
        roles: [],
        userId: '',
        applicationStatus: '',
        permissionsArr: [],
        foundries: [],
        defaultFoundry: {},
        sessionFoundryName: '',
        sessionFoundryId: '',
        impersonate: false,
        emailVerified: false,
        gracePeriodExpired: false
      },
      processing: false,
      logInProcessing: false,
      cancelTokens: [],
      sameAPITokens: {},
      logoutUser: false,
      isSignUp: false,
      isUserAdmin: CONSOLIDATED.NONE,
      foundriesTerminated: CONSOLIDATED.NONE,
      showContractTerminationModal: false,
      contractTerminationModalProps: null
    }
  },

  getters: {
    getLogoutState(state) {
      return state.logoutUser
    },
    cancelTokens(state) {
      return state.cancelTokens
    },
    user: state => {
      return state.user
    },
    getRole: state => state.user.roles,
    hasRole: state => {
      return roles => {
        /**
         * @param {Array|String} roles – permission roles
         * @returns {Boolean}
         */
        if (typeof roles === 'string') {
          roles = [roles];
        } else if (!Array.isArray(roles)) {
          roles = [];
        }
        let hasRole = false
        for (const role of state.user.roles) {
          roles.some(r => {
            if (r === role) {
              hasRole = true
              return true
            }
          })
        }
        return hasRole
      }
    },
    getLogInProcessingStatus: state => state.logInProcessing,
    isUserAuthenticated: state => state.user.authenticated,
    getPermissionsArr: state => state.user.permissionsArr,
    sessionFoundryName: state => state.sessionFoundryName,
    sessionFoundryId: state => state.sessionFoundryId,
    getAuthRoles: state => state.user.roles,
    isEmailVerified: state => state.user.emailVerified,
    isGracePeriodExpired: state => state.user.gracePeriodExpired,
    isSignUp: state => state.isSignUp,
    isUserAdmin: state => state.isUserAdmin,
    foundriesTerminated: state => state.foundriesTerminated,
    showContractTerminationModal: state => state.showContractTerminationModal,
    contractTerminationModalProps: state => state.contractTerminationModalProps,
    isContractLocked: (state, getters) => {
      if (getters.hasRole(['foundrySupport'])) return false
      const lockLevel = getNavLockLevel({
        foundriesTerminated: state.foundriesTerminated,
        isUserAdmin: state.isUserAdmin
      })
      return lockLevel === NAV_LOCK_LEVEL.PARTIAL || lockLevel === NAV_LOCK_LEVEL.FULL
    }
  },

  mutations: {
    addCancelToken(state, token) {
      state.cancelTokens.push(token)
    },
    clearCancelTokens(state) {
      state.cancelTokens = []
    },
    addSameAPITokens(state, obj) {
      let arr = obj.config.url.split('/')
      let endPoint = arr[arr.length - 1]
      state.sameAPITokens[endPoint] = obj.source
    },
    cancelSameAPITokens(state, endPoints) {
      endPoints.forEach(endPoint => {
        state.sameAPITokens[endPoint]?.cancel()
      })
    },

    setAuth(state, options) {
      options = options || {}
      state.user.authenticated = options.status === true
      Api.defaults.headers.Authorization = options.token || ''
      state.user.name = options.name || ''
      state.user.displayName = (options.displayName || '').trim()
      state.user.roles = options.roles || []
      state.user.userId = options.userId
      state.user.applicationStatus = options.applicationStatus
      state.user.permissionsArr = options.permissionsArr
      state.user.impersonate = false
      state.user.emailVerified = options.emailVerified || false
      state.user.gracePeriodExpired = options.gracePeriodExpired || false
      // state.user.foundries = options.foundries
    },
    setApplicationStatus(state, status) {
      state.user.applicationStatus = status
    },
    setUserRole(state, role = ['foundryAdmin']) {
      state.user.roles = role
      window.localStorage.setItem('roles', JSON.stringify(role))
    },
    setPermissionsArr(state, permissionsArr) {
      state.user.permissionsArr = permissionsArr
    },
    setLogInProcessing(state, status) {
      state.logInProcessing = status
    },
    submitStart(state) {
      state.processing = true
    },
    submitComplete(state) {
      state.processing = false
    },
    setFoundries(state, data) {
      state.user.foundries = data
    },
    setDefaultFoundry(state, data) {
      state.user.defaultFoundry = { ...data }
    },
    clearStorage() {
      window.localStorage.clear();
    },
    sessionName(state, name) {
      state.sessionFoundryName = name
    },
    sessionId(state, id) {
      state.sessionFoundryId = id
    },
    setImpersonateUser(state) {
      state.user.impersonate = true
      window.localStorage.setItem('FS-Impersonate', state.user.impersonate)
    },
    unSetImpersonateUser(state) {
      state.user.impersonate = false
      window.localStorage.removeItem('FS-Impersonate')
      window.localStorage.removeItem('impersonatedFoundry')
      state.logoutUser = true
    },
    setIsSignUp(state, value) {
      state.isSignUp = value
    },
    clearIsSignUp(state) {
      state.isSignUp = false
    },
    setEmailVerified(state, value) {
      state.user.emailVerified = value === true;
    },
    setGracePeriodExpired(state, value) {
      state.user.gracePeriodExpired = !!value;
    },
    setContractTerminationMeta(state, { isUserAdmin, foundriesTerminated }) {
      state.isUserAdmin = isUserAdmin || CONSOLIDATED.NONE
      state.foundriesTerminated = foundriesTerminated || CONSOLIDATED.NONE
    },
    setContractTerminationModal(state, { show, props }) {
      state.showContractTerminationModal = show
      state.contractTerminationModalProps = props || null
    }
  },

  actions: {
    cancelPendingRequests(context) {
      // Cancel all request where a token exists
      context.state.cancelTokens.forEach((request, i) => {
        if (request.cancel) {
          request.cancel()
        }
      })

      // Reset the cancelTokens store
      context.commit('clearCancelTokens')
    },
    login({ commit, state }, credentials) {
      if (!state.processing) {
        return new Promise((resolve, reject) => {
          commit('clearStorage')
          commit('submitStart')
          commit('setLogInProcessing', true)
          Api.Login.login(credentials)
            .then(response => {
              if (response.data.success === true) {
                // Save user data in local storage for later usage
                // GoogleTagManager.add({
                //   'event': 'User Identified',
                //   'User Name': response.data.name
                // })
                // store user-assigned foundries in later versions

                window.localStorage.setItem('api_token', response.data.apiToken)
                window.localStorage.setItem('current_user', response.data.name || '')
                window.localStorage.setItem('display_name', (response.data.displayName || '').trim())
                window.localStorage.setItem('userId', response.data.userId)
                updateUserInfoObjKey('userId', response.data.userId)
                window.localStorage.setItem('roles', JSON.stringify(response.data.roles))
                updateUserInfoObjKey('roles', response.data.roles)
                window.localStorage.setItem('application_status', response.data.applicationStatus)
                window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(response.data.foundries)))
                window.localStorage.setItem('loginPic', response.data.profilePic)
                window.localStorage.setItem('emailVerified', response.data.emailVerified);
                if(!response.data.emailVerified){
                  window.localStorage.setItem(EMAIL_VERIFY_BANNER_DISMISSED_STORAGE_KEY, false);
                }
                window.localStorage.setItem('gracePeriodExpired', response.data.gracePeriodExpired);
                commit('setFoundries', response.data.foundries)
                commit('setDefaultFoundry', response.data.defaultFoundry)
                commit('profile/setFoundries', response.data, { root: true })
                commit('setAuth', {
                  status: true,
                  token: response.data.apiToken,
                  // foundries: response.data.foundries,
                  name: response.data.name,
                  displayName: (response.data.displayName || '').trim(),
                  userId: response.data.userId,
                  roles: response.data.roles,
                  applicationStatus: response.data.applicationStatus,
                  permissionsArr: Misc.getPermissionsArr(response.data.foundries),
                  emailVerified: response.data.emailVerified,
                  gracePeriodExpired: response.data.emailVerified ? false : response.data.gracePeriodExpired
                })
                commit('setIsSignUp', response.data.isSignUp || false)

                const ctIsUserAdmin = response.data.isUserAdmin || CONSOLIDATED.NONE
                const ctFoundriesTerminated = response.data.foundriesTerminated || CONSOLIDATED.NONE
                window.localStorage.setItem('isUserAdmin', ctIsUserAdmin)
                window.localStorage.setItem('foundriesTerminated', ctFoundriesTerminated)
                window.localStorage.setItem('userFoundries', JSON.stringify(response.data.foundries || []))
                commit('setContractTerminationMeta', { isUserAdmin: ctIsUserAdmin, foundriesTerminated: ctFoundriesTerminated })

                commit('submitComplete')
                commit('setLogInProcessing', false)
                resolve(response)
              }
              if (response.data.success === false) {
                commit('submitComplete')
                commit('setLogInProcessing', false)
                reject(Errors.create())
              }
            })
            .catch((error) => {
              commit('submitComplete')
              commit('setLogInProcessing', false)
              reject(Errors.create(error))
            })
        })
      }
    },

    // signup (context, creds, redirect) {
    //   context.$http.post(SIGNUP_URL, creds, (data) => {
    //     // window.localStorage.setItem('api_token', data.apiToken)
    //     // this.user.authenticated = true
    //     if (redirect) {
    //       router.go(redirect)
    //     }
    //   }, (err) => {
    //     context.error = err
    //   })
    // },//

    logout({ commit }) {
      return new Promise((resolve, reject) => {
        Api.Login.logout()
          .then((response) => {
            if (response.data.success === true) {
              Api.defaults.headers.Authorization = ''
              commit('clearStorage')
              commit('setAuth')
              commit('unSetImpersonateUser')
              resolve()
            }
            if (response.data.success === false) {
              reject(Errors.create())
            }
          })
          .catch((error) => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },

    getUserDetails({ commit }) {
      return new Promise((resolve) => {
        Api.Profile.getProfile()
          .then((response) => {
            persistLocalStorageFromProfileResponse(response.data);
            if (response.data) {
              commit('setEmailVerified', response.data.emailVerified);
              commit('setGracePeriodExpired', response.data.gracePeriodExpired);
            }
          })
          .catch(() => {
          console.log('[auth/getUserDetails] Profile fetch failed');
          })
          .finally(() => resolve());
      });
    },

    checkAuth({ commit }) {
      return new Promise(resolve => {
        const jwt = window.localStorage.getItem('api_token')
        const name = window.localStorage.getItem('current_user')
        const displayName = window.localStorage.getItem('display_name')
        const userId = window.localStorage.getItem('userId')
        const roles = window.localStorage.getItem('roles')
        const applicationStatus = window.localStorage.getItem('application_status')
        const permissionsArr = JSON.parse(window.localStorage.getItem('permissionsArr'))
        const emailVerified = getEmailVerifiedFromLocalStorage();
        const gracePeriodExpired = window.localStorage.getItem('gracePeriodExpired') === 'true'
        const gracePeriodExpiresAt = window.localStorage.getItem('gracePeriodExpiresAt')
        commit('setAuth', {
          status: !!(jwt),
          token: (jwt === null) ? '' : jwt,
          name: (name === null || name === 'undefined') ? '' : name,
          displayName: (displayName === null || typeof displayName === 'undefined') ? '' : (displayName || '').trim(),
          userId: (userId === null || userId === 'undefined') ? '' : userId,
          roles: (roles === null || roles === 'undefined') ? [] : JSON.parse(roles),
          applicationStatus,
          permissionsArr,
          emailVerified,
          gracePeriodExpired,
          gracePeriodExpiresAt: gracePeriodExpiresAt || null
          // , foundries: foundries
        })

        const ctIsUserAdmin = window.localStorage.getItem('isUserAdmin') || CONSOLIDATED.NONE
        const ctFoundriesTerminated = window.localStorage.getItem('foundriesTerminated') || CONSOLIDATED.NONE
        commit('setContractTerminationMeta', { isUserAdmin: ctIsUserAdmin, foundriesTerminated: ctFoundriesTerminated })

        resolve()
      })
    },

    setPermissionsArr({ commit }, permissionsArr) {
      return commit('setPermissionsArr', permissionsArr)
    },

    setSessionFoundry({ commit }, foundry = {}) {
      return new Promise((resolve, reject) => {
        if (Object.keys(foundry).length > 0) {
          Api.Users.setSessionFoundryForUser({ id: foundry.id })
            .then((response) => {
              if (response.data.success === true) {
                window.localStorage.setItem('sessionFoundryName', foundry.name)
                window.localStorage.setItem('sessionFoundryId', foundry.id)
                commit('sessionName', foundry.name)
                commit('sessionId', foundry.id)
                commit('profile/setFoundrySessionName', {}, { root: true })
                commit('profile/setFoundrySessionId', {}, { root: true })
                const foundries = JSON.parse(window.localStorage.getItem('userFoundries')) || [];
                const defaultFoundry = foundries.find(f => f.id === foundry.id) || {};
                commit('profile/setBannerURL', defaultFoundry.bannerUrl, { root: true })
                commit('profile/setLogoURL', defaultFoundry.logoUrl, { root: true })
                // Delay the update to ensure localStorage changes are fully committed
                // A setTimeout with 100ms gives plenty of buffer in case the previous setItem was called at the end of the current tick
                // As we will be reading from LS in common function getSessionFoundryAiConsentFromLS
                setTimeout(() => {
                  commit('profile/setFoundryAiConsent', getSessionFoundryAiConsentFromLS(), { root: true })
                }, 100)
                resolve(response)
              }
              if (response.data.success === false) {
                reject(Errors.create())
              }
            })
            .catch((error) => {
              let err = Errors.create(error)
              commit('errors/add', err)
              reject(err)
            })
        }
      })
    },

    sendEmailVerification() {
      return Api.Users.sendEmailVerification()
    }
  },

  modules: {
    errors: VuexErrors
  }
}
